import React, { useCallback, useEffect, useState } from 'react'
import { IonContent, IonHeader, IonToolbar, IonTitle, IonIcon, IonButtons, useIonPopover } from '@ionic/react'
import { Button, List, Text } from '@mytours/components'
import { getDirMode, isRTL } from 'mobile_web/modules/platform'
import { ionHistory } from 'mobile_web/modules/navigation'
import { closeOutline as closeIcon } from 'ionicons/icons'
import { EventActionsEnum, sendEvent } from 'mobile_web/modules/logging/analytics'
import { Collection } from 'mytours-read-common'

import { popoverEnterAnimation, popoverLeaveAnimation } from './animations'

import strings from 'mobile_web/strings'

import classNames from 'classnames'
import './styles.less'

interface Props {
	fullScreen?: boolean
	title?: string
	collection: Collection
	content: JSX.Element
	menuToggleRef?: (ref: HTMLElement) => void
}

interface MenuProps {
	title: string | undefined
	content: JSX.Element
	onDismiss: () => void
}

const Menu: React.FC<MenuProps> = ({ title, content, onDismiss }) => (
	<IonContent className="menu-body" forceOverscroll={false}>
		<IonHeader className="header">
			<IonToolbar className="toolbar">
				<IonTitle className="toolbar-title">
					<h1>{title}</h1>
				</IonTitle>
				<IonButtons slot="end" className="header-buttons">
					<Button variant="clear" aria-label={strings.close} className="menu-toggle" onClick={onDismiss}>
						{/* Decorative: the button is named by aria-label and the Text below. */}
						<IonIcon className="icon" icon={closeIcon} aria-hidden="true" />
						<Text variant="inherit" className="hidden">
							{strings.accessibility_go_back}
						</Text>
					</Button>
				</IonButtons>
			</IonToolbar>
		</IonHeader>

		<List variant="ios" className="list" dir={getDirMode()}>
			{content}
		</List>
	</IonContent>
)

export const ToggleMenu: React.FC<Props> = ({ title, content, fullScreen, menuToggleRef }) => {
	const [present, dismiss] = useIonPopover(Menu, {
		title,
		content,
		onDismiss: () => dismiss(),
		style: {
			[isRTL() ? '--left-positon' : '--right-position']: 'calc(max(0px, 50% - var(--max-page-width) / 2))',
		},
	})

	useEffect(() => {
		let prevPath = ionHistory.location.pathname
		let prevSearch = ionHistory.location.search

		const unListen = ionHistory.listen(({ pathname, search }) => {
			if (prevPath !== pathname || prevSearch !== search) {
				dismiss()
			}
			prevPath = pathname
			prevSearch = search
		})

		return () => unListen()
	}, [dismiss])

	return (
		<div className="toggle-menu-container">
			<Button
				variant="clear"
				aria-label={strings.open_menu}
				className="menu-toggle"
				onClick={() =>
					present({
						side: 'bottom',
						alignment: 'end',
						cssClass: classNames('popover-menu', { 'full-screen': !!fullScreen, rtl: isRTL() }),
						enterAnimation: popoverEnterAnimation,
						leaveAnimation: popoverLeaveAnimation,
					})
				}
				ref={menuToggleRef}
			>
				<i className="icon fal fa-list-ul" />
			</Button>
		</div>
	)
}
